import { useCallback, useEffect, useMemo, useState } from "react";

import { useURL } from "~/contexts";

import {
  clearRefreshAttempt,
  readRefreshAttempt,
  writeRefreshAttempt,
} from "./tpa-balance-refresh.client";
import {
  needsForcedRefresh,
  shouldMarkRefreshAttempted,
  tpaBalanceResourcePath,
} from "./tpa-balance-refresh.logic";
import type { TpaBalanceSession, UseTpaBalanceResource } from "./types";

/**
 * The page's TPA-balance fetch, scoped to ONE shopper (COM-674).
 *
 * The provider sits in the layout, so it survives a client-side logout/login;
 * without the identity reset below the previous shopper's balance stayed on
 * screen until a hard refresh. The first fetch after a login asks for a forced
 * MuleSoft refresh, at most once per login per tab (see
 * `tpa-balance-refresh.logic.ts`).
 *
 * `useResource` must be a stable, module-level hook (it is called every render).
 */
export function useTpaBalanceSession<T>(
  shopperId: string | null,
  useResource: UseTpaBalanceResource<T>,
): TpaBalanceSession<T> {
  const url = useURL();

  const [wanted, setWanted] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);
  // Shopper the current fetch state belongs to, and the payload object that was
  // on screen when that shopper changed (hidden until a new fetch replaces it —
  // the fail-soft hook deliberately never clears `data` itself).
  const [ownerId, setOwnerId] = useState(shopperId);
  const [stalePayload, setStalePayload] = useState<T | undefined>(undefined);

  const href = wanted ? url(tpaBalanceResourcePath(forceRefresh)) : null;
  const { data, isLoading } = useResource(href, reloadKey);

  // Shopper changed (logout, login, account switch): drop the previous
  // shopper's balance and disarm until a consumer re-requests for the new one.
  // Render-phase state adjustment (React "adjusting state on prop change") so
  // the stale balance never paints for even one commit.
  if (ownerId !== shopperId) {
    setOwnerId(shopperId);
    setStalePayload(data);
    setWanted(false);
    setForceRefresh(false);
    setReloadKey(k => k + 1);
  }

  // A guest render (logout, or a full page load after one) re-arms the forced
  // refresh for whoever logs in next in this tab. An SFRA-side logout followed
  // by a login without a Workers guest render in between is not seen here —
  // the server-side follow-up in the client module would cover it.
  useEffect(() => {
    if (!shopperId) clearRefreshAttempt();
  }, [shopperId]);

  // `shopperId` in the deps gives `request` a new identity per shopper, so a
  // consumer whose `enabled` stays true across the switch (ASIFLEX cookie
  // shopper) still re-runs its opt-in effect and re-arms the fetch.
  const needsRefresh = useCallback(
    () => needsForcedRefresh(shopperId, readRefreshAttempt()),
    [shopperId],
  );
  const request = useCallback(() => {
    if (needsRefresh()) setForceRefresh(true);
    setWanted(true);
  }, [needsRefresh]);
  // Re-deriving the flag here folds the href change into the reload's own
  // fetch instead of costing an extra one; once the attempt is marked, reloads
  // (basket changes) read the stored balance.
  const reload = useCallback(() => {
    setWanted(true);
    setForceRefresh(needsRefresh());
    setReloadKey(k => k + 1);
  }, [needsRefresh]);

  const visibleData = data === stalePayload ? undefined : data;
  // Settled with nothing for this shopper: a cold failure, or a failed re-fetch
  // after a same-tab re-login (the hook kept the previous shopper's object,
  // which stays hidden). Without it a skeleton consumer waits forever.
  const failed = href !== null && !isLoading && visibleData === undefined;

  // The flag stays set until the next reload so marking doesn't change `href`
  // and cost a second fetch.
  useEffect(() => {
    if (
      shopperId &&
      shouldMarkRefreshAttempted({ forceRefresh, shopperId, isLoading })
    ) {
      writeRefreshAttempt(shopperId);
    }
  }, [forceRefresh, shopperId, isLoading]);

  return useMemo(
    () => ({ data: visibleData, isLoading, failed, request, reload }),
    [visibleData, isLoading, failed, request, reload],
  );
}
